import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGovernance } from '../../context/GovernanceContext';
import { DIMENSIONS } from '../../data/questions';
import { getMaturity, formatScore, MATURITY_LEVELS } from '../../utils/scoring';

function ScoreBar({ score, color }) {
  const pct = score ? (score / 5) * 100 : 0;
  return (
    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        style={{ width: `${pct}%`, backgroundColor: color }}
        className="h-full rounded-full transition-all"
      />
    </div>
  );
}

const STATUS_LABELS = {
  todo: 'To do',
  'in-progress': 'In progress',
};

const STATUS_ICON = {
  todo: '○',
  'in-progress': '●',
};

export default function ClientReport() {
  const { id } = useParams();
  const {
    clients,
    loading,
    getClientActions,
    getClientDimensionScore,
    getClientOverallScore,
  } = useGovernance();

  const client = clients.find((c) => c.id === id);

  if (loading) return <div className="text-mid-grey text-sm p-8">Loading…</div>;
  if (!client) return (
    <div className="text-center py-16">
      <p className="text-mid-grey">Client not found.</p>
      <Link to="/clients" className="text-brand-teal text-sm hover:underline mt-2 block">
        ← Back to clients
      </Link>
    </div>
  );

  const overall = getClientOverallScore(client.id);
  const overallMaturity = getMaturity(overall);
  const allActions = getClientActions(client.id);
  const outstanding = allActions.filter((a) => a.status !== 'done');
  const dateStr = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto print-page">
      {/* No-print controls */}
      <div className="no-print flex items-center justify-between mb-6 gap-4">
        <Link
          to={`/clients/${client.id}`}
          className="text-sm text-brand-teal hover:underline"
        >
          ← Back to overview
        </Link>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90 transition-colors"
        >
          Print / Save as PDF
        </button>
      </div>

      {/* ── Report header ─────────────────────────────────────────── */}
      <div className="bg-brand-navy text-white rounded-xl p-6 mb-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/60 mb-1">
              AIGov · AI Governance Assessment Report
            </div>
            <h1 className="text-2xl font-black">{client.name}</h1>
            <div className="text-sm text-white/70 mt-1">
              Singapore IMDA Agentic AI Framework 2026
            </div>
          </div>
          <div className="text-right text-sm text-white/60 whitespace-nowrap">
            {dateStr}
          </div>
        </div>
      </div>

      {/* ── Executive summary ─────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 border-t-0 rounded-b-none p-6 mb-0">
        <h2 className="text-xs font-bold tracking-widest uppercase text-mid-grey mb-4">
          Executive Summary
        </h2>
        <div className="flex items-center gap-4 mb-3">
          <div className="text-4xl font-black" style={{ color: overallMaturity.color }}>
            {formatScore(overall)}
          </div>
          <div className="text-lg text-gray-400 font-light">/ 5</div>
          <div className="flex-1">
            <ScoreBar score={overall} color={overallMaturity.color} />
          </div>
        </div>
        <div
          className="inline-block text-sm font-semibold px-3 py-1 rounded-full"
          style={{ backgroundColor: `${overallMaturity.color}20`, color: overallMaturity.color }}
        >
          {overallMaturity.label}
        </div>
        <p className="text-sm text-mid-grey mt-3">
          This report summarises the current AI governance maturity across four dimensions of the
          Singapore IMDA Agentic AI Framework. Scores are based on question-level assessments
          completed to date.
        </p>
      </div>

      {/* ── Dimension scores ──────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 border-t-0 p-6 mb-0">
        <h2 className="text-xs font-bold tracking-widest uppercase text-mid-grey mb-4">
          Dimension Scores
        </h2>
        <div className="space-y-4">
          {DIMENSIONS.map((dim) => {
            const score = getClientDimensionScore(client.id, dim.num);
            const maturity = getMaturity(score);
            return (
              <div key={dim.num} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: dim.color }}>
                  {dim.num}
                </div>
                <div className="w-44 flex-shrink-0 text-sm font-medium text-brand-navy truncate">
                  {dim.label}
                </div>
                <ScoreBar score={score} color={dim.color} />
                <div className="w-10 text-right text-sm font-bold flex-shrink-0"
                  style={{ color: maturity.color }}>
                  {formatScore(score)}
                </div>
                <div className="w-28 flex-shrink-0">
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${maturity.color}20`, color: maturity.color }}
                  >
                    {maturity.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Maturity scale legend */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="text-xs text-mid-grey mb-2">Maturity scale</div>
          <div className="flex flex-wrap gap-3">
            {MATURITY_LEVELS.map((m) => (
              <div key={m.score} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
                <span className="text-xs text-gray-600">{m.score} – {m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Action plan ───────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 border-t-0 rounded-b-xl p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xs font-bold tracking-widest uppercase text-mid-grey">
            Action Plan
          </h2>
          {outstanding.length > 0 && (
            <span className="text-xs text-mid-grey">
              {outstanding.length} outstanding action{outstanding.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {outstanding.length === 0 ? (
          <p className="text-sm text-mid-grey italic">
            All actions are complete. No outstanding items.
          </p>
        ) : (
          <div className="space-y-6">
            {DIMENSIONS.map((dim) => {
              const dimOutstanding = outstanding.filter((a) => a.dimension === dim.num);
              if (dimOutstanding.length === 0) return null;

              const doneCount = allActions.filter(
                (a) => a.dimension === dim.num && a.status === 'done'
              ).length;

              return (
                <div key={dim.num}>
                  <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-100">
                    <div
                      className="w-1 h-5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: dim.color }}
                    />
                    <span className="text-sm font-semibold text-brand-navy">
                      D{dim.num} · {dim.label}
                    </span>
                    <span className="text-xs text-mid-grey ml-auto">
                      {dimOutstanding.length} outstanding
                      {doneCount > 0 && `, ${doneCount} done`}
                    </span>
                  </div>

                  <div className="space-y-3 pl-3">
                    {dimOutstanding.map((action) => (
                      <div key={action.id} className="flex gap-3">
                        <span
                          className="text-base flex-shrink-0 mt-0.5"
                          style={{ color: action.status === 'in-progress' ? dim.color : '#9CA3AF' }}
                        >
                          {STATUS_ICON[action.status] ?? '○'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm font-medium text-brand-navy">
                              {action.title}
                            </span>
                            <span
                              className="text-xs font-semibold px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: action.status === 'in-progress'
                                  ? `${dim.color}20` : '#F3F4F6',
                                color: action.status === 'in-progress'
                                  ? dim.color : '#6B7280',
                              }}
                            >
                              {STATUS_LABELS[action.status] ?? action.status}
                            </span>
                          </div>
                          {action.description && (
                            <p className="text-xs text-mid-grey mt-0.5 leading-relaxed">
                              {action.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <div className="mt-4 text-center text-xs text-mid-grey pb-8">
        Prepared with AIGov Dashboard · {dateStr}
      </div>
    </div>
  );
}
